import { SKILL_FIELD_TYPE_ALIAS, SKILL_PROPERTY_NAME_ALIAS } from './constants';
import {
  InitSkillPropertyType,
  InitSkillType,
  SkillFieldType,
  SkillPropertyAlias,
  SkillInputProperty,
  SkillPropertyType,
  SkillsInputsConfig,
  SkillType,
  SkillInputConfig,
} from './types';

export const isKnownSkillPropertyName = (name: string) => {
  return Boolean(SKILL_PROPERTY_NAME_ALIAS[name as SkillPropertyAlias]);
};

export const isKnownSkillField = (fieldType: string) => {
  return Boolean(SKILL_FIELD_TYPE_ALIAS[fieldType as SkillFieldType]);
};

// Filter config
const filterSkillProperties = (properties: SkillInputProperty[]): SkillInputProperty[] => {
  return properties
    .filter(prop => isKnownSkillPropertyName(prop.propertyName) && isKnownSkillField(prop.fieldType))
    .map(prop => ({
      ...prop,
      name: prop.propertyName,
      properties: filterSkillProperties(prop.properties || []),
    }));
};

export const getValidSkillsConfig = (skillsConfig: SkillInputConfig[]) => {
  return {
    skills: skillsConfig.map(skill => ({
      ...skill,
      properties: filterSkillProperties(skill.properties || []),
    })),
  };
};
// end

// Transform properties to object with name
export const transformProperties = (properties: InitSkillPropertyType[]): Dictionary<SkillPropertyType[]> => {
  const filteredProperties = properties.filter(item => isKnownSkillPropertyName(item.name));
  return filteredProperties.reduce((acc, prop) => {
    if (!acc[prop.name]) {
      acc[prop.name] = [];
    }
    acc[prop.name].push({
      id: prop.id,
      name: prop.name,
      value: prop.value,
      properties: transformProperties(prop.properties),
    });
    return acc;
  }, {} as any);
};

export const transformSkills = (skills: InitSkillType[]): SkillType[] => {
  return skills.map(skill => ({
    ...skill,
    properties: transformProperties(skill.properties),
  }));
};
// end

// Transform to init (for request) helpers
const flattenProperties = (propsObj: Record<string, SkillPropertyType[]>): InitSkillPropertyType[] => {
  return Object.entries(propsObj).flatMap(([name, propArray]) =>
    propArray.flatMap(prop => {
      if (!prop.value) return []; // Skip if empty
      return [
        {
          id: prop.id ?? undefined,
          name,
          value: prop.value,
          properties: flattenProperties(prop.properties || {}),
        },
      ];
    }),
  ) as InitSkillPropertyType[];
};

export const mapToTargetFormat = (input: SkillType[]): InitSkillType[] => {
  return input.map(skill => ({
    id: skill.id ?? undefined,
    skillName: skill.skillName,
    status: skill.status,
    properties: flattenProperties(skill.properties || {}),
  }));
};
// end

// Build Validation shema  by skill config
export function mapConfigToValidationShema(config: SkillsInputsConfig) {
  return {
    type: 'array',
    required: false,
    forEach: {
      type: 'object',
      properties: {
        skillName: {
          type: 'string',
          required: true,
        },
        status: {
          type: 'string',
          required: true,
        },
        properties: {
          type: 'object',
          properties: config?.skills?.reduce((acc, skill) => {
            skill.properties.forEach(prop => {
              if (!acc[prop.propertyName]) {
                acc[prop.propertyName] = {
                  type: 'array',
                  // required: true,
                  forEach: {
                    type: 'object',
                    required: true,
                    properties: buildNestedProperties(prop),
                  },
                };
              }
            });
            return acc;
          }, {} as any),
        },
      },
    },
  };
}

function buildNestedProperties(prop: SkillInputProperty) {
  if (prop.properties && prop.properties.length > 0) {
    const nestedProps = prop.properties.reduce((acc, nestedProp) => {
      acc[nestedProp.propertyName] = {
        type: 'array',
        // required: true,
        forEach: {
          type: 'object',
          // required: true,
          properties: buildNestedProperties(nestedProp),
        },
      };
      return acc;
    }, {} as any);

    return {
      value: {
        type: 'string',
        required: !prop.optional,
      },
      properties: {
        type: 'object',
        // required: true,
        properties: nestedProps,
      },
    };
  } else {
    return {
      value: {
        type: 'string',
        required: !prop.optional,
      },
    };
  }
}
// end

// Build Default values by skill config
export const buildDefaultProperties = (properties: SkillInputProperty[]): Dictionary<SkillPropertyType[]> => {
  const result = {} as Dictionary<SkillPropertyType[]>;

  properties.forEach(prop => {
    result[prop.propertyName] = [
      {
        value: '',
        ...(prop.properties?.length > 0 && {
          properties: buildDefaultProperties(prop.properties),
        }),
      },
    ];
  });

  return result;
};
// end

// Remove ids and status for find duplicates
export const removeIdsAndStatus = (obj: InitSkillType): Omit<InitSkillType, 'id' | 'status'> => {
  if (Array.isArray(obj)) {
    return obj.map(removeIdsAndStatus) as unknown as Omit<InitSkillType, 'id' | 'status'>;
  } else if (obj && typeof obj === 'object') {
    const newObj = {} as Omit<InitSkillType, 'id' | 'status'>;
    for (const [key, value] of Object.entries(obj)) {
      if (key !== 'id' && key !== 'status') {
        (newObj as any)[key] = removeIdsAndStatus(value as any);
      }
    }
    return newObj;
  }
  return obj as Omit<InitSkillType, 'id' | 'status'>;
};

// duplicates
export const findDuplicateIndexes = (arr: InitSkillType[]): number[][] => {
  const map = new Map();
  arr.forEach((item, i) => {
    const normalized = JSON.stringify(removeIdsAndStatus(item));
    if (!map.has(normalized)) {
      map.set(normalized, []);
    }
    map.get(normalized).push(i);
  });

  return [...map.values()].filter(indices => indices.length > 1);
};
